import React, { useState, useEffect } from "react";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import { useItemsFilter } from "../../../_helper/actions/app/items";
import { ApiEndPoint } from "../../../_helper/constant/api-types";
import { toast } from "react-toastify";
import { useAtom } from "jotai";
import { pharmacyAtom } from "../../../_helper/constant/constant-atom";
import {
  useCreateItem,
  useUpdateItem,
} from "../../../_helper/actions/mutation/items";

const ScheduleTaggingForm = ({
  setIsModalOpen,
  medicine, // if present, form is in "edit" mode
}) => {
  // Initialize form state based on whether we're editing or adding
  const initialFormData = {
    generic_name: medicine?.generic_name || "",
    drug_name: medicine?.drug_name || "",
    drugId: medicine?.drugId || "",
    schedule: medicine?.schedule || "",
    is_prescription_required: medicine?.is_prescription_required || "No",
  };

  const [formData, setFormData] = useState(initialFormData);
  const [medicineOptions, setMedicineOptions] = useState([]);
  const [complianceTrackingOptions, setComplianceTrackingOption] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [pharmacy] = useAtom(pharmacyAtom);
  // State to track if the selected/created medicine is new
  const [isNewMedicine, setIsNewMedicine] = useState(false);

  const { data: ComplianceTracking } = useItemsFilter(
    ApiEndPoint.PHARMACY,
    "?action=getComplianceTracking"
  );

  const { data: medicineData } = useItemsFilter(
    ApiEndPoint.SEARCH_MEDICINE_DATA,
    `?medicine_name=${searchQuery}`,
    searchQuery ? true : false
  );

  const { data: getMedinceDetails } = useItemsFilter(
    ApiEndPoint.MEDICINE_DATA,
    `?action=getMedicineData&id=${formData.drugId}&medicine_data=True`
  );

  const createMutation = useCreateItem(ApiEndPoint.PHARMACY);
  const updateMutation = useUpdateItem(ApiEndPoint.PHARMACY);

  // Update medicine options when search results are available.
  // Merge newly created options (or previously selected suggestions) with fetched ones.
  useEffect(() => {
    if (medicineData && Array.isArray(medicineData)) {
      setMedicineOptions((prevOptions) => {
        // Keep options that were created or previously selected (even if not in the current fetched list)
        const preservedOptions = prevOptions.filter(
          (opt) => opt.isNew || opt.preserved
        );
        const fetchedOptions = medicineData.map((med) => ({
          value: med.id,
          label: med.medicine_name,
          isNew: false,
        }));
        return [...preservedOptions, ...fetchedOptions];
      });
    }
  }, [medicineData]);

  // Update schedule options when compliance tracking data is available
  useEffect(() => {
    if (ComplianceTracking && Array.isArray(ComplianceTracking)) {
      setComplianceTrackingOption(
        ComplianceTracking.map((item) => ({
          value: item.schedule,
          label: item.schedule,
          is_prescription_required: item.is_prescription_needed,
        }))
      );
    }
  }, [ComplianceTracking]);

  // Auto-update generic_name from medicine details (only if not already set by edit)
  useEffect(() => {
    if (getMedinceDetails && getMedinceDetails.generic_name) {
      setFormData((prev) => ({
        ...prev,
        generic_name: getMedinceDetails.generic_name.name || "",
      }));
    }
  }, [getMedinceDetails]);

  // If the "medicine" prop changes (editing an existing record), update the form state
  useEffect(() => {
    if (medicine) {
      setFormData({
        generic_name: medicine.generic_name || "",
        drug_name: medicine.drug_name || "",
        drugId: medicine.drugId || "",
        schedule: medicine.schedule || "",
        is_prescription_required: medicine.is_prescription_required || "No",
      });
      // When editing an existing medicine, assume it's not new
      setIsNewMedicine(false);
    } else {
      // Reset to blank state when switching to "add" mode
      setFormData({
        generic_name: "",
        drug_name: "",
        drugId: "",
        schedule: "",
        is_prescription_required: "No",
      });
      setIsNewMedicine(false);
    }
  }, [medicine]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.drug_name || !formData.schedule) {
      toast.error("Please fill in all required fields.");
      return;
    } else {
      const payload = {
        action: medicine ? "updateScheduleTagging" : "postScheduleTagging",
        pharmacy_id: pharmacy.id,
        ...formData,
        ...(medicine && { id: medicine.id }),
      };
      if (medicine && medicine.id) {
        // Update the existing medicine tag
        updateMutation.mutate(payload, {
          onSuccess: () => {
            toast.success("The medicine tag has been updated successfully!");
          },
        });
      } else {
        // Create a new medicine tag
        createMutation.mutate(payload, {
          onSuccess: () => {
            toast.success("The medicine has been tagged successfully!");
          },
        });
      }
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleScheduleChange = (selected) => {
    const selectedOption = complianceTrackingOptions.find(
      (option) => option.value === selected?.value
    );
    setFormData((prev) => ({
      ...prev,
      schedule: selected?.value || "",
      is_prescription_required:
        selectedOption?.is_prescription_required || "No",
    }));
  };

  // Updated handleMedicineChange: now also handles clearing the field.
  const handleMedicineChange = (selectedOption) => {
    if (!selectedOption) {
      // If cleared, reset the drug_name and drugId fields.
      setFormData((prev) => ({ ...prev, drug_name: "", drugId: "" }));
      setIsNewMedicine(false);
      return;
    }

    // If a suggestion (or created option) is selected, add it to the medicineOptions list
    // (mark it as preserved) so it stays in the options.
    setMedicineOptions((prevOptions) => {
      const exists = prevOptions.find(
        (option) => option.value === selectedOption.value
      );
      if (!exists) {
        return [...prevOptions, { ...selectedOption, preserved: true }];
      }
      return prevOptions;
    });
    setFormData((prev) => ({
      ...prev,
      drug_name: selectedOption.label || "",
      drugId: selectedOption.value || "",
    }));
    setIsNewMedicine(selectedOption.isNew || false);
  };

  const handleSearchChange = (inputValue) => {
    setSearchQuery(inputValue);
  };

  const handleCreateMedicine = (inputValue) => {
    const newOption = {
      value: inputValue.toLowerCase(),
      label: inputValue,
      isNew: true,
      preserved: true,
    };
    setMedicineOptions((prevOptions) => [...prevOptions, newOption]);
    setFormData((prev) => ({
      ...prev,
      drug_name: newOption.label,
      drugId: "",
    }));
    setIsNewMedicine(true);
  };

  // console.log(formData);
  return (
    <form className="space-y-4" onSubmit={handleSubmit}>
      {/* Drug Name */}
      <div>
        <label className="block text-gray-700 font-medium">
          Drug Name <span className="text-red-500">*</span>
        </label>
        {medicine && medicine?.id ? (
          <input
            type="text"
            value={formData.drug_name}
            className="w-full border border-gray-300 rounded-md p-2 mt-1 bg-gray-100 cursor-not-allowed"
          />
        ) : (
          <CreatableSelect
            isClearable
            options={medicineOptions}
            onChange={handleMedicineChange}
            onCreateOption={handleCreateMedicine}
            onInputChange={handleSearchChange}
            isSearchable
            placeholder="Search or Add Medicine"
            value={
              formData.drugId
                ? medicineOptions.find(
                    (option) => option.value === formData.drugId
                  ) || null
                : formData.drug_name
                  ? medicineOptions.find(
                      (option) =>
                        option.label.toLowerCase() ===
                        formData.drug_name.toLowerCase()
                    ) || null
                  : null
            }
            className="mt-1"
          />
        )}
      </div>

      {/* Generic Name */}
      <div>
        <label className="block text-gray-700 font-medium">
          Generic Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          name="generic_name"
          value={formData.generic_name}
          onChange={handleChange}
          className="w-full border rounded p-2 mt-1"
          placeholder="Enter Generic Name"
          // Allow editing if a new medicine is being added; otherwise, keep it read-only
          readOnly={!isNewMedicine}
        />
      </div>

      {/* Schedule */}
      <div>
        <label className="block text-gray-700 font-medium">
          Schedule <span className="text-red-500">*</span>
        </label>
        <Select
          options={complianceTrackingOptions}
          onChange={handleScheduleChange}
          isSearchable
          placeholder="Select Schedule"
          value={
            formData.schedule
              ? complianceTrackingOptions.find(
                  (option) => option.value === formData.schedule
                ) || null
              : null
          }
          className="mt-1"
        />
      </div>

      {/* Prescription Required */}
      <div>
        <label className="block text-gray-700 font-medium">
          Prescription Required
        </label>
        <select
          name="is_prescription_required"
          value={formData.is_prescription_required}
          readOnly
          className="w-full border rounded p-2 mt-1 bg-gray-100 cursor-not-allowed"
        >
          <option value="Yes">Yes</option>
          <option value="No">No</option>
        </select>
      </div>

      {/* Submit Button */}
      <div className="flex justify-end">
        <button
          type="submit"
          className="bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700"
        >
          {medicine ? "Update Medicine" : "Submit"}
        </button>
      </div>
    </form>
  );
};

export default ScheduleTaggingForm;
